import React, { Fragment, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import stringsService from '../../services/strings-service';

const UploadImage = (props) => {

  /* Refs */
  const inputFileRef = useRef(null);

  /* State */
  const [imageUrl, setImageUrl] = useState(props.iniImageUrl);
  const [videoEmbedUrl, setVideoEmbedUrl] =
    useState(props.includeVideo ? props.iniVideoEmbedUrl : '');
  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(props.disabled);
  const [externalVideoUrl, setExternalVideoUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [encodingProgress, setEncodingProgress] = useState(null);

  /* Other vars */
  let encodingProgressFilename = null;
  let checkEncodingProgressInterval = null;

  /* Events */
  const uploadBtClickHandler = (event) => {
    if (disabled) return;
    inputFileRef.current.click();
  }
  const inputFileChangeHandler = (event) => {
    const formData = new FormData();
    const file = event.target.files[0];
    if (file) {
      formData.append('file', file, file.name);
      uploadImageOrVideo(formData);
      event.target.value = '';
    }
  }
  const inputExternalUrlChangeHandler = (event) => {
    setExternalVideoUrl(event.target.value);
  }
  const saveExternalUrlClickHandler = (event) => {
    const formData = new FormData();
    formData.append('external_video_url', externalVideoUrl);
    uploadImageOrVideo(formData);
  }
  const uploadProgressEventHandle = (progressEvent) => {
    setUploadProgress(Math.floor(
      progressEvent.loaded / progressEvent.total * 100));
  }

  /* Methods */
  /* Expects formData to be class FromData */
  const uploadImageOrVideo = (formData) => {
    if (disabled) return;
    for (const i in props.formData) {
      formData.append(i, props.formData[i]);
    }
    encodingProgressFilename = stringsService.getRandomString(24);
    checkEncodingProgressInterval = setInterval(checkEncodingProgress, 1000);
    formData.append('encoding_progress_filename', encodingProgressFilename);
    setIsLoading(true);
    setUploadProgress(null);
    setEncodingProgress(null);
    axios.post(props.uploadUrl, formData, {
        onUploadProgress: uploadProgressEventHandle,
      })
      .then((response) => {
        if (response.status === 200) {
          emptyValues();
          if (response.data.imageUrl) {
            setImageUrl(response.data.imageUrl);
          }
          if (response.data.videoEmbedUrl) {
            setVideoEmbedUrl(response.data.videoEmbedUrl);
          }
        } else {
          alert("Unkown error");
        }
      }).catch((error) => {
        setIsLoading(false);
        if (error.response.data.message) {
          alert(error.response.data.message);
        } else {
          alert(error);
        }
      }).finally(() => {
        setIsLoading(false);
        clearInterval(checkEncodingProgressInterval);
        encodingProgressFilename = '';
      });
  }
  const checkEncodingProgress = () => {
    if (props.encodingProgressUrl && encodingProgressFilename) {
      axios.post(props.encodingProgressUrl, {
        encoding_progress_filename: encodingProgressFilename,
      }).then((response) => {
          if (response.data.progress) {
            setEncodingProgress(response.data.progress);
          }
        });
    }
  }
  const emptyValues = () => {
    setImageUrl('');
    setVideoEmbedUrl('');
    setExternalVideoUrl('');
  }

  /* Image classes */
  const imgClasses = ['img-fluid', 'mx-auto'];
  if (props.imageRounded) {
    imgClasses.push('rounded-circle');
  }

  /* Image or spinner (or video) */
  let imageOrSpinner;
  if (isLoading) {
    imageOrSpinner = (<Fragment>
      <span className="spinner"></span>
      <span className="upload-progress">
        <span>{encodingProgress === null ? uploadProgress : encodingProgress }%</span>
      </span>
      <span className="progress-type">
        {encodingProgress === null ? 'Uploading' : 'Encoding'}
      </span>
    </Fragment>);
  } else {
    if (videoEmbedUrl) {
      imageOrSpinner = (<iframe src={videoEmbedUrl} allowfullscreen />);
    } else if (imageUrl.match(/\.mp4$/)) {
      imageOrSpinner = (<video className={imgClasses.join(' ')} controls>
        <source src={imageUrl+'#t=0.1'} type="video/mp4"/>
        Your browser does not support video</video>);
    } else {
      imageOrSpinner = (<img className={imgClasses.join(' ')}
        src={imageUrl} width="300" height="300" alt="" />);
    }
  }

  /* Include videos or only images */
  let uploadButtonLabel;
  let externalVideoInput;
  if (props.includeVideo) {
    uploadButtonLabel = 'Upload picture/video';
    externalVideoInput = (
      <div className={"mt-4" + (disabled ? " disabled" : "")}>
        <label htmlFor="upload-image-external-url">Enter Youtube URL or ID</label>
        <div className="form-group d-flex">
          <input type="text" id="upload-image-external-url" className="form-control flex-grow-1"
            name="external-url" value={externalVideoUrl} onChange={inputExternalUrlChangeHandler}/>
          <button className="btn btn-outline-primary" disabled={isLoading} onClick={saveExternalUrlClickHandler}>Save</button>
        </div>
      </div>
    );
  } else {
    uploadButtonLabel = 'Upload picture';
    externalVideoInput = null;
  }

  /* Disabled or not */
  return (
    <Fragment>
      <div className={"gap-2 mt-2 mb-2" + (disabled ? " disabled" : "")}>
        <div className="dashboard-avatar-wrapper">
          <div className="dashboard-avatar">
            {imageOrSpinner}
          </div>
        </div>
      </div>
      <div className={"mt-4 d-flex" + (disabled ? " disabled" : "")}>
        <input type="file" className="form-control" name="profile-picture" ref={inputFileRef}
          onChange={inputFileChangeHandler} style={{display: 'none'}} />
        <button className="btn btn-outline-primary btn-block flex-grow-1"
          onClick={uploadBtClickHandler} disabled={isLoading}>{uploadButtonLabel}</button>
      </div>
      {externalVideoInput}
      {false ?
        (<div className={"mt-4 text-center" + (disabled ? " disabled" : "")}>
          <button className="btn btn-outline-danger" disabled={isLoading}>Delete</button>
        </div>)
        : null}
    </Fragment>
  );
}

UploadImage.propTypes = {
  iniImageUrl: PropTypes.string.isRequired,
  iniVideoEmbedUrl: PropTypes.string, // required if includeVideo is true
  uploadUrl: PropTypes.string.isRequired,
  encodingProgressUrl: PropTypes.string,
  formData: PropTypes.object.isRequired,
  imageRounded: PropTypes.bool,
  includeVideo: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default UploadImage;
